import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Button from '@mui/material/Button'
import { Divider } from '@mui/material'
import EastIcon from '@mui/icons-material/East'
import WestIcon from '@mui/icons-material/West'
import { courseSubjectApiSlice } from '../../../../../features/course/courseSubjectSlice'
import {
    CourseSubjectPricePurchaseState,
    PurchaseState,
    PurchasedCourseTopicState,
    SelectedPurchaseSubjectState,
} from '../../../../../features/types'
import SelectSubjectTab from './selectSubjectTab/SelectSubjectTab'
import SelectTopicTab from './selectTopicTab/SelectTopicTab'
import SelectLanguageTab from './selectLanguageTab/SelectLanguageTab'
import { flexRow, fullWidth, stepperBox } from '../style'
import { SelectedSubjectState } from '../types'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
    style?: any
}

interface Props {
    coursePriceId: number
    queryCourseSubjectId: number
    activeStep: number
    purchaseResponse: PurchaseState
    selectedLanguage: string
    selectedSubjects: SelectedSubjectState[]
    queryParamsExists: boolean
    setActiveStep: React.Dispatch<React.SetStateAction<number>>
    onSubjectClick: (
        subject: SelectedPurchaseSubjectState,
        courseSubjectPrice: CourseSubjectPricePurchaseState,
    ) => void
    setSelectedSubjects: (subjects: SelectedSubjectState[]) => void
    onSelectTopics: (
        topics: PurchasedCourseTopicState[],
        subjectId: number,
    ) => void
    handleLanguage: (language: string) => void
    onTopicClick: (
        topic: PurchasedCourseTopicState,
        subjectId: number,
        topics?: PurchasedCourseTopicState[],
    ) => void
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box
                    sx={{
                        p: 3,
                        '@media screen and (max-width: 500px)': {
                            p: 1,
                        },
                    }}
                >
                    {children}
                </Box>
            )}
        </div>
    )
}

const CheckoutStepper: React.FC<Props> = ({
    queryCourseSubjectId,
    coursePriceId,
    activeStep,
    purchaseResponse,
    selectedLanguage,
    selectedSubjects,
    queryParamsExists,
    setActiveStep,
    onSubjectClick,
    handleLanguage,
    setSelectedSubjects,
    onSelectTopics,
    onTopicClick,
}) => {
    const { formatMessage } = useIntl()
    const steps = [
        formatMessage({ id: 'language' }),
        formatMessage({ id: 'subject' }),
        formatMessage({ id: 'topic' }),
    ]

    const [completed, setCompleted] = React.useState<{
        [k: number]: boolean
    }>({ 0: false })

    const [subjects, setSubjects] = useState([])

    const [getCourseSubjectPurchaseOptions] =
        courseSubjectApiSlice.useGetCourseSubjectsPurchaseOptionsMutation()

    const courseSubjectIds = purchaseResponse.courseSubjectIds || []

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1)
    }

    const handleStep = (step: number) => () => {
        setActiveStep(step)
    }

    const handleComplete = () => {
        const newCompleted = completed
        newCompleted[activeStep] = true
        setCompleted(newCompleted)
        handleNext()
    }

    const getSelectedSubjectsOption = async () => {
        try {
            const purchasedSubjects = await getCourseSubjectPurchaseOptions({
                coursePriceId,
                courseSubjectIds,
            }).unwrap()

            setSelectedSubjectsOptions(purchasedSubjects)
            setSubjects(purchasedSubjects)
        } catch (error) {}
    }

    const setSelectedSubjectsOptions = (
        purchasedSubjects: SelectedPurchaseSubjectState[],
    ) => {
        const selectedSubjects = purchasedSubjects.reduce(
            (result: SelectedSubjectState[], subject) => {
                const isQueryCourseSubject =
                    subject.courseSubjectId === queryCourseSubjectId
                if (
                    (subject.isSelected && !queryParamsExists) ||
                    isQueryCourseSubject
                ) {
                    const courseSubjectPrice =
                        purchaseResponse.courseSubjectPrices.find(
                            (price) =>
                                price.courseSubjectId ===
                                subject.courseSubjectId,
                        )

                    result.push({
                        ...subject,
                        isSelected: !queryParamsExists
                            ? subject.isSelected
                            : false,
                        courseSubjectPrice,
                        selectedTopics: [],
                    })
                }

                return result
            },
            [],
        )

        setSelectedSubjects(selectedSubjects)
    }

    const handleToggleAllTopics = (subjectId: number) => {
        const toggledSubjects = selectedSubjects.map((subject) => {
            if (subject.id === subjectId) {
                if (subject.isSelected) {
                    return {
                        ...subject,
                        isSelected: false,
                        selectedTopics: [],
                    }
                }

                return {
                    ...subject,
                    isSelected: true,
                    selectedTopics: [],
                }
            }

            return subject
        })

        setSelectedSubjects(toggledSubjects)
    }

    useEffect(() => {
        if (purchaseResponse.courseSubjectPrices.length) {
            getSelectedSubjectsOption()
        }
    }, [purchaseResponse])

    useEffect(() => {
        if (selectedLanguage) {
            setCompleted((prev) => ({ ...prev, [0]: true }))
        }
    }, [selectedLanguage])

    useEffect(() => {
        const isSelected = !!selectedSubjects.length
        setCompleted((prev) => ({ ...prev, [1]: isSelected }))
    }, [selectedSubjects])

    return (
        <Box sx={fullWidth}>
            <Box sx={stepperBox}>
                <Stepper
                    alternativeLabel
                    activeStep={activeStep}
                >
                    {steps.map((label, index) => (
                        <Step
                            key={label}
                            completed={completed[index]}
                        >
                            <StepButton
                                color="inherit"
                                onClick={handleStep(index)}
                            >
                                {label}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box sx={{ minHeight: '500px' }}>
                <TabPanel
                    value={activeStep}
                    index={0}
                >
                    <SelectLanguageTab
                        selectedLanguage={selectedLanguage}
                        handleLanguage={handleLanguage}
                    />
                </TabPanel>

                <TabPanel
                    value={activeStep}
                    index={1}
                >
                    <SelectSubjectTab
                        selectedSubjects={selectedSubjects}
                        onSubjectClick={onSubjectClick}
                        subjects={subjects}
                        courseSubjectPrices={
                            purchaseResponse.courseSubjectPrices
                        }
                        duration={purchaseResponse.coursePrice.duration}
                    />
                </TabPanel>

                <TabPanel
                    value={activeStep}
                    index={2}
                >
                    <SelectTopicTab
                        selectedLanguage={selectedLanguage}
                        selectedSubjects={selectedSubjects}
                        coursePriceId={coursePriceId}
                        queryParamsExists={queryParamsExists}
                        queryCourseSubjectId={queryCourseSubjectId}
                        onTopicClick={onTopicClick}
                        handleToggleAllTopics={handleToggleAllTopics}
                        onSelectTopics={onSelectTopics}
                        promotionDiscount={
                            purchaseResponse?.coursePromotion?.discount
                        }
                    />
                </TabPanel>
            </Box>

            <Box
                sx={flexRow}
                pt={2}
            >
                <Button
                    variant="outlined"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                >
                    <WestIcon
                        fontSize="small"
                        sx={{ mr: 1 }}
                    />

                    {formatMessage({ id: 'back' })}
                </Button>

                <Box sx={{ flex: '1 1 auto' }} />

                <Button
                    variant="contained"
                    disabled={!completed[activeStep] || activeStep === 2}
                    onClick={handleComplete}
                    sx={{ mr: 1 }}
                >
                    {formatMessage({ id: 'further' })}

                    <EastIcon
                        fontSize="small"
                        sx={{ ml: 1 }}
                    />
                </Button>
            </Box>

            <Divider sx={{ pb: 2 }} />
        </Box>
    )
}

export default CheckoutStepper
